package top.wcpe.allincore.ui.runtime.render

import top.wcpe.allincore.ui.runtime.internal.text.charCountOf
import top.wcpe.allincore.ui.runtime.internal.text.codePointAtCompat
import kotlin.jvm.JvmField
import kotlin.jvm.JvmName
import kotlin.jvm.JvmStatic
import kotlin.math.abs
import kotlin.math.floor

/**
 * 文本布局 / 命中纯函数（单行 + 多行）。
 *
 * 职责：给定原始文本、对齐起始 X（`textX`）、测宽函数与可选密码掩码，提供「字符偏移 ↔ 像素 X」双向映射：
 * - [caretXFor]：某 code unit 偏移处的像素 X（= `textX` + 显示前缀宽度）；
 * - [caretXSnapshot]：产出各偏移光标 X 的快照数组，交给输入派发器做无测宽的列定位；
 * - [columnAtSnapshot]：在快照数组上把鼠标像素 X 映射到最近的合法字符偏移
 *   （取最近边界并吸附到 code point 边界，超左=0、超右=len）。这是命中列定位的**唯一权威实现**，
 *   渲染期发布快照、派发期点击都走它。
 *
 * 密码掩码：测宽用「显示文本」（掩码后），但返回 / 接收的偏移始终是真实文本的 code unit 偏移。
 * 偏移永不落在代理对中间（不劈裂 emoji 等增补平面字符）。
 *
 * 无平台 / 渲染依赖：测宽通过 [Measurer] 注入，可单测。
 *
 * 多行布局（[multiline]）在单行基元之上扩展：用 [TextWrapper] 把文本按 `\n`
 * 硬换行 + 可用宽度软换行切成视觉行，并维护「视觉行行首真实偏移 ↔ (行, 行内像素 X)」双向映射，
 * 供多行渲染、上下行光标移动、点击 / 拖选命中复用——行内像素 X 与命中算法直接复用单行同款逻辑（DRY）。
 */
@Suppress("TooManyFunctions")
class TextLayout private constructor(
    private val text: String,
    private val textX: Float,
    private val measurer: Measurer,
    private val mask: Mask,
    /** 多行视觉行表；为 `null` 表示本实例是单行布局。 */
    private val visualLines: List<VisualLine>?,
    /** 多行顶部起始 Y；单行布局下无意义。 */
    private val topY: Float,
    /** 多行行高（像素）；单行布局下无意义。 */
    private val lineHeight: Float,
) {

    /**
     * 测宽函数：返回字符串在目标字体 / 缩放下的像素宽度。
     *
     * 渲染侧用 `{ s -> bridge.textWidth(s, 1f) }` 适配，测试侧可注入固定每字符宽度。
     */
    fun interface Measurer {
        fun width(text: String): Float
    }

    /** 字符掩码函数：把真实字符串映射为等长显示字符串（密码模式遮蔽）；非密码模式传恒等映射。 */
    fun interface Mask {
        fun display(text: String): String
    }

    /**
     * 一条视觉行：硬换行（`\n`）或软换行（按宽度）切出的单行。
     *
     * @property text        本行文本（不含分行用的 `\n`）
     * @property startOffset 本行首字符在原始文本中的真实 code unit 偏移
     * @property y           本行顶部像素 Y
     */
    data class VisualLine(
        @get:JvmName("text") val text: String,
        @get:JvmName("startOffset") val startOffset: Int,
        @get:JvmName("y") val y: Float,
    )

    /**
     * 某 code unit 偏移处光标的像素 X。
     *
     * = `textX` + 「真实文本前缀经掩码后的显示宽度」。偏移越界时钳制到 `[0,len]`。
     */
    fun caretXFor(offset: Int): Float {
        val clamped = clampToBoundary(offset)
        if (clamped <= 0) {
            return textX
        }
        return textX + measurer.width(mask.display(text.substring(0, clamped)))
    }

    /**
     * 产出每个 code unit 偏移处的光标像素 X 快照，长度 = `text.length() + 1`（含末尾）。
     *
     * 用于命中缓存：渲染期一次性算好 X 边界数组，交给输入派发器做「像素 X → 字符列」的纯查找，
     * 避免派发器持有渲染 bridge。代理对中间的偏移（低代理那一位）填**该码点左缘**的 X，
     * 与前一合法边界一致；最近边界查找由 [columnAtSnapshot] 显式跳过这些非法偏移，故二者口径自洽。
     *
     * @param base 各 X 减去的基准（如渲染期传 `box.x()` 得到相对 box 左缘的偏移；传 0 得到绝对 X）
     */
    fun caretXSnapshot(base: Float): FloatArray = buildCaretXArray(text, textX - base, measurer, mask)

    // ====================== 多行布局 ======================

    /** 视觉行列表（多行布局）；单行布局调用抛 [IllegalStateException]。 */
    fun visualLines(): List<VisualLine> = requireMultiline()

    /**
     * 产出多行命中布局快照：把每条视觉行的光标 X 数组 + 行元数据打包成不持渲染依赖的
     * [MultilineHitLayout]，供渲染器发布给 `TextField`、派发期做纯查找式命中（点击 / 拖选 / 上下移动）。
     *
     * **坐标相对化（与单行 [caretXSnapshot] 同口径）**：每条行的 X 减去 `xBase`、
     * Y 减去 `yBase`，使快照与派发期的局部坐标系（`localX = mx - box 左缘`、
     * `localY = my - box 顶缘`）同源。渲染侧传 `box.x()` / `box.y()` 即可。
     *
     * 仅多行布局可调；单行布局抛 [IllegalStateException]。
     *
     * @param xBase 各行内 X 减去的基准（通常传 `box.x()`）
     * @param yBase 各行 Y 减去的基准（通常传 `box.y()`）
     */
    fun hitLayout(xBase: Float, yBase: Float): MultilineHitLayout {
        val lines = requireMultiline()
        val hits = lines.map { line ->
            val xs = lineSnapshot(line)
            val rel = FloatArray(xs.size) { xs[it] - xBase }
            MultilineHitLayout.LineHit(line.text, line.startOffset, line.y - yBase, rel)
        }
        return MultilineHitLayout(hits, topY - yBase, lineHeight)
    }

    /**
     * 真实偏移 → 光标坐标 `(x, y)`。
     *
     * 先定位偏移所属视觉行，再用单行同式 `textX + measurer(mask(行内前缀))` 求行内 X，
     * Y 取该视觉行顶部 Y。
     *
     * 软换行点归属约定：偏移恰好落在某软换行边界时归属**后一行行首**（即取行内列 0），
     * 与「光标停在行首」的直觉一致；硬换行（`\n`）的 `\n` 字符本身归前一行行尾，
     * `\n` 之后的偏移归后一行行首。
     *
     * @return 长度 2 的数组 `[x, y]`
     */
    fun caretXYFor(offset: Int): FloatArray {
        requireMultiline()
        val clamped = clampToBoundary(offset)
        val line = lineForOffset(clamped)
        val x = caretXOnLine(line, clamped - line.startOffset)
        return floatArrayOf(x, line.y)
    }

    /**
     * 点击像素 `(pixelX, pixelY)` → 最近的合法真实偏移。
     *
     * 先按 `pixelY` 选中视觉行（钳制到首 / 末行），再在该行内复用
     * [columnAtSnapshot] 做「像素 X → 行内列」的最近边界吸附（含代理对吸附 / 跳低代理），
     * 行内列加上行首偏移得到真实偏移。
     */
    fun offsetAt(pixelX: Double, pixelY: Double): Int {
        requireMultiline()
        val line = lineForY(pixelY)
        val inLine = columnAtSnapshot(lineSnapshot(line), line.text, pixelX)
        return line.startOffset + inLine
    }

    /**
     * 上 / 下移动光标：在相邻视觉行按 `desiredX`（保持的像素列）找最近偏移。
     *
     * 供 Up / Down 键「保持像素列」用：先定位当前偏移所属视觉行，取上 / 下相邻行，
     * 在目标行内按 `desiredX` 复用 [columnAtSnapshot] 吸附最近边界；
     * 已在首行上移 / 末行下移则返回原偏移（不越界）。
     *
     * @param offset   当前真实偏移
     * @param dir      `-1` 上移、`+1` 下移
     * @param desiredX 期望保持的绝对像素列（通常取移动起点的 [caretXYFor] 之 x）
     * @return 新的真实偏移
     */
    fun verticalMove(offset: Int, dir: Int, desiredX: Float): Int {
        val lines = requireMultiline()
        val clamped = clampToBoundary(offset)
        val target = rowForOffset(clamped) + if (dir < 0) -1 else 1
        if (target < 0 || target >= lines.size) {
            return clamped
        }
        val line = lines[target]
        val inLine = columnAtSnapshot(lineSnapshot(line), line.text, desiredX.toDouble())
        return line.startOffset + inLine
    }

    /**
     * 某视觉行内某列（行内 code unit 偏移）处的绝对像素 X。
     *
     * 供多行选区高亮逐行求两端 X：必须按本行内列计算，**不**走 [caretXYFor]——后者对软换行
     * 边界偏移会归到后一行行首（返回 textX），用于求「本行行尾 X」会得到错误的下一行行首 X。
     *
     * @param line   目标视觉行
     * @param inLine 行内 code unit 偏移（钳制到 `[0, 行长]`）
     */
    fun caretXOnVisualLine(line: VisualLine, inLine: Int): Float {
        requireMultiline()
        return caretXOnLine(line, inLine)
    }

    /** 行内某列（行内 code unit 偏移）处的绝对像素 X。 */
    private fun caretXOnLine(line: VisualLine, inLine: Int): Float {
        val clamped = inLine.coerceIn(0, line.text.length)
        if (clamped <= 0) {
            return textX
        }
        return textX + measurer.width(mask.display(line.text.substring(0, clamped)))
    }

    /** 产出某视觉行的行内光标 X 快照（绝对基准，base=0），口径同 [caretXSnapshot]。 */
    private fun lineSnapshot(line: VisualLine): FloatArray = // 复用共享构造器：行内为绝对基准，初始累加值取 textX（等价于 base=0）。
        buildCaretXArray(line.text, textX, measurer, mask)

    /** 偏移所属视觉行：取「行首偏移 ≤ offset」中最靠后者（软换行边界归后一行）。 */
    private fun lineForOffset(offset: Int): VisualLine = requireMultiline()[rowForOffset(offset)]

    /** 偏移所属视觉行序号：软换行边界归后一行行首，硬换行 \n 字符归前一行行尾。 */
    private fun rowForOffset(offset: Int): Int {
        val lines = requireMultiline()
        for (i in lines.indices) {
            val line = lines[i]
            val end = line.startOffset + line.text.length
            if (offset <= end) {
                // offset 落在本行 [start,end]；若恰等于 end 且后一行行首也等于 end（软换行），归后一行
                val softWrapToNext = offset == end && i + 1 < lines.size && lines[i + 1].startOffset == end
                return if (softWrapToNext) i + 1 else i
            }
        }
        return lines.size - 1
    }

    /** 像素 Y 选中的视觉行：按行高分桶，钳制到 `[首行, 末行]`。 */
    private fun lineForY(pixelY: Double): VisualLine {
        val lines = requireMultiline()
        if (lineHeight <= 0f) {
            return lines[0]
        }
        val idx = floor((pixelY - topY) / lineHeight).toInt()
        return lines[idx.coerceIn(0, lines.size - 1)]
    }

    /** 校验多行布局并返回视觉行表；单行布局抛 [IllegalStateException]。 */
    private fun requireMultiline(): List<VisualLine> = visualLines ?: error("当前 TextLayout 为单行布局，不支持多行操作")

    /** 把偏移钳制到 `[0,len]` 并对齐 code point 边界（落在低代理上则回退一位）。 */
    private fun clampToBoundary(offset: Int): Int {
        val len = text.length
        val p = offset.coerceIn(0, len)
        val splitsSurrogate = p in 1 until len &&
            text[p].isLowSurrogate() &&
            text[p - 1].isHighSurrogate()
        return if (splitsSurrogate) p - 1 else p
    }

    companion object {
        /** 恒等掩码：显示文本 = 真实文本。 */
        @JvmField
        val NO_MASK = Mask { it }

        /**
         * 构造单行布局。
         *
         * @param text     真实文本（含密码模式下的明文）
         * @param textX    文本对齐起始像素 X（与渲染侧 valueTextX 同基准）
         * @param measurer 测宽函数
         * @param mask     字符掩码（非密码模式传 [NO_MASK]）
         */
        @JvmStatic
        fun singleLine(text: String?, textX: Float, measurer: Measurer, mask: Mask?): TextLayout =
            TextLayout(text ?: "", textX, measurer, mask ?: NO_MASK, null, 0f, 0f)

        /**
         * 构造多行布局：按 `\n` 硬换行 + `availableWidth` 软换行切分视觉行，
         * 并记录每条视觉行的行首真实偏移与像素 Y。
         *
         * 软换行复用 [TextWrapper.wrap]（与按钮多行同一实现）；行首真实偏移由本方法
         * 按「段落起点 + 段内累加」还原（`wrap` 丢弃 `\n`，故 `\n` 在段落之间占 1 个 code unit）。
         *
         * **多行坐标基准（重要：M4b 接线契约）**：所有多行 API（[caretXYFor]、[offsetAt]、
         * [verticalMove]）的 X / Y 一律采用**绝对像素坐标系**，与单行 [caretXFor] 同口径——
         * X 以 `textX` 为零点累加显示前缀宽度、Y 以 `topY` 为首行顶部按 `lineHeight` 递增。
         * 这区别于单行 [caretXSnapshot] 的「相对 base」口径：单行快照可减去一个 `base`
         * （如 box 左缘）得到相对偏移，而多行内部行快照固定取 `base=0`（绝对），故多行**输入**的
         * `pixelX/pixelY/desiredX` 与**输出**的 `[x,y]` 都是绝对像素。
         *
         * 接线时务必把两端基准对齐到本布局的绝对系：构造时 `textX` 传渲染侧的 `valueTextX`、
         * `topY` 传首行渲染顶部 Y；调用 [offsetAt] 前需把屏幕命中 `localX/localY`
         * 换算回同一绝对系（即加上 box 左缘 / 顶部、抵消滚动偏移），不可直接传 box 内相对坐标，否则命中错位。
         *
         * @param text           真实文本（含 `\n` 与密码模式明文）
         * @param textX          文本对齐起始像素 X（每条视觉行行内 X 的绝对基准，对齐渲染侧 valueTextX）
         * @param topY           首行顶部像素 Y（多行 Y 的绝对基准）
         * @param lineHeight     行高（相邻视觉行 Y 间距）
         * @param availableWidth 可用宽度；`<= 0` 时仅按 `\n` 分行不软换行
         * @param measurer       测宽函数
         * @param mask           字符掩码（非密码模式传 [NO_MASK]）
         */
        @JvmStatic
        fun multiline(
            text: String?,
            textX: Float,
            topY: Float,
            lineHeight: Float,
            availableWidth: Float,
            measurer: Measurer,
            mask: Mask?,
        ): TextLayout {
            val src = text ?: ""
            val m = mask ?: NO_MASK
            val lines = buildVisualLines(src, topY, lineHeight, availableWidth, measurer, m)
            return TextLayout(src, textX, measurer, m, lines, topY, lineHeight)
        }

        /**
         * 命中列定位的**唯一权威实现**：在 `caretXRel` 快照数组上把局部像素 X 映射到最近的合法字符 code unit 偏移。
         *
         * 纯静态、无渲染依赖：渲染期由 [caretXSnapshot] 发布快照，派发期点击 / 拖选 / 双击都委托本方法，
         * 保证「运行期真用的逻辑」与「单测覆盖的逻辑」是同一份。
         *
         * 代理对吸附：返回的偏移必须落在合法 code point 边界——若候选偏移 `off` 处 `text[off]`
         * 为低代理（即落在代理对中间），则跳过该偏移、不参与最近边界比较。于是点击 emoji 右半吸附到其**之后**、
         * 左半吸附到其**之前**，绝不返回劈裂中间偏移。
         *
         * @param caretXRel 渲染期发布的各偏移光标 X 快照（长度 = text 长度 + 1）；为 null/空时返回 0
         * @param text      与快照对应的真实文本（用于判定代理对边界）；为 null 按空串处理
         * @param localX    与快照同基准系的局部鼠标 X
         * @return 最近的合法 code unit 偏移（超左=0、超右=len）
         */
        @JvmStatic
        fun columnAtSnapshot(caretXRel: FloatArray?, text: String?, localX: Double): Int {
            if (caretXRel == null || caretXRel.isEmpty()) {
                return 0
            }
            val t = text ?: ""
            var best = 0
            var bestDist = abs(localX - caretXRel[0])
            for (i in 1 until caretXRel.size) {
                // 跳过落在代理对中间的偏移（i 处为低代理）：它不是合法 code point 边界，不参与比较
                if (i < t.length && t[i].isLowSurrogate()) {
                    continue
                }
                val dist = abs(localX - caretXRel[i])
                if (dist < bestDist) {
                    bestDist = dist
                    best = i
                }
            }
            return best
        }

        /**
         * 共享的光标 X 快照构造器：对 `lineText` 按 **code point** 累加，
         * 产出长度 `lineText.length() + 1` 的各偏移光标 X 数组。
         *
         * 单行 [caretXSnapshot] 与多行 [lineSnapshot] 复用本函数，消除复制粘贴。
         * 二者唯一差异是初始累加值 `startX`：单行传 `textX - base`（相对基准），
         * 多行行内传 `textX`（绝对基准、base=0）。
         *
         * 代理对处理：先把代理对中间的偏移（`i+1..next-1`）填为码点**左缘**（与 `xs[i]` 一致），
         * 再在加宽后把右边界 `xs[next]` 填为右缘；这样劈裂中间偏移不会被吸到右缘而误命中，
         * 与 [columnAtSnapshot] 跳过低代理偏移的口径自洽。
         */
        private fun buildCaretXArray(lineText: String, startX: Float, measurer: Measurer, mask: Mask): FloatArray {
            val len = lineText.length
            val xs = FloatArray(len + 1)
            var accum = startX
            xs[0] = accum
            var i = 0
            while (i < len) {
                val cp = lineText.codePointAtCompat(i)
                val next = i + charCountOf(cp)
                for (k in i + 1 until next) {
                    xs[k] = accum
                }
                accum += measurer.width(mask.display(lineText.substring(i, next)))
                xs[next] = accum
                i = next
            }
            return xs
        }

        /** 按段落 + 段内软换行还原每条视觉行的行首真实偏移。 */
        private fun buildVisualLines(
            text: String,
            topY: Float,
            lineHeight: Float,
            availableWidth: Float,
            measurer: Measurer,
            mask: Mask,
        ): List<VisualLine> {
            val lines = mutableListOf<VisualLine>()
            // 软换行测宽需走掩码显示串，与单行行内 X 口径一致（密码模式下按掩码宽度断行）。
            val wrapMeasurer = Measurer { measurer.width(mask.display(it)) }
            var paragraphStart = 0 // 当前段落首字符的真实偏移
            var visualRow = 0 // 视觉行序号（决定 Y）
            for (paragraph in text.split("\n")) {
                var pieceStart = paragraphStart // 当前软换行片段首字符的真实偏移
                for (piece in TextWrapper.wrap(paragraph, availableWidth, wrapMeasurer)) {
                    lines.add(VisualLine(piece, pieceStart, topY + visualRow * lineHeight))
                    pieceStart += piece.length
                    visualRow++
                }
                // 跨到下一段落：本段长度 + 1 个 \n
                paragraphStart += paragraph.length + 1
            }
            return lines
        }
    }
}
